import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, User, Mail, Phone, Briefcase } from 'lucide-react';
import { PageHeader, Button, Modal, Input, BackToListLink } from '@/components/shared/ui';
import { classKeys } from '../../data/queries/useClasses';
import { ClassItem } from '../../data/classes';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../lib/constants/routes';
import { getLocalizedClassItem, classDetailDtoToClassItem } from '@/utils/classUtils';
import { getClassDetail } from '@/services/classService';
import { getApiLanguageCode } from '@/lib/i18nLocale';
import { formatCurrency, formatCurrencyForLocale } from '@/lib/currencyFormat';
import { ApiError } from '@/services/apiClient';
import { orderService, buildUpdateOrderDtoFromDetail } from '@/services/orderService';
import { enrollClass } from '@/services/classService';
import { getTossClientKey, isTossCheckoutEnabled } from '@/config/tossPayments';
import { queryClient } from '@/lib/queryClient';
import { invalidateEnrolledClassListQueries } from '@/data/queries/useClasses';
import { parseCartStorage, persistCartItems } from '@/utils/cartStorage';
import { saveTossCheckoutPending } from '@/lib/tossPendingCheckout';
import {
    buildTossMerchantOrderId,
    requestTossCardCheckout,
    tossMerchantOrderIdFromOpenClassOrderNumber,
} from '@/services/tossCheckout';
import { pushRecentStudentOrderSummary } from '@/lib/recentStudentOrders';
import type { ApiPaymentStatus } from '@/types/api/order';
import type { TossCheckoutSnapshotDto } from '@/types/api/payment';

function buildCheckoutSnapshot(orderItems: Array<{ class: ClassItem; quantity: number }>): TossCheckoutSnapshotDto {
    const items = orderItems
        .map(({ class: c, quantity }) => {
            const classId = parseInt(String(c.id), 10);
            if (!Number.isFinite(classId) || classId <= 0) return null;
            return {
                classId,
                ...(typeof c.productPriceId === 'number' && c.productPriceId > 0 ? { productPriceId: c.productPriceId } : {}),
                ...(c.productId?.trim() ? { productId: c.productId.trim() } : {}),
                quantity,
            };
        })
        .filter((x): x is NonNullable<typeof x> => x != null);
    const seen = new Set<number>();
    const classIds: number[] = [];
    for (const it of items) {
        if (!seen.has(it.classId)) {
            seen.add(it.classId);
            classIds.push(it.classId);
        }
    }
    return {
        classIds,
        items,
        clientApp: 'medicrew-web',
    };
}

export interface OrderConfirmItem {
    classId: string;
    quantity: number;
}

/**
 * react-router location.state — 장바구니·클래스상세 등에서 navigate 시 전달.
 * - items 또는 selectedClassIds 중 하나는 필수.
 * - totalPrice: 네비게이션 시점 합계(상세 GET 후 ClassItem 기반 payableFromItems가 있으면 그쪽이 우선).
 * - POST /order/open-class(OpenAPI PurchaseOpenClassRequestDto)는 classIds만 받음 — 금액은 백엔드가 클래스 가격으로 산정.
 */
interface OrderConfirmState {
    items?: OrderConfirmItem[];
    selectedClassIds?: string[];
    totalPrice?: number;
}

/** 대한병원협회 회원 상태: none=인증 전, verified=인증됐으나 정회원 아님, full=정회원 */
const KHA_MEMBER_STATUS_KEY = 'kha_member_status';

/**
 * 로컬에서만: 주문 금액을 고정해 토스·POST 검증만 할 때 사용.
 * 미설정·빈 값 → 고정 안 함(클래스 상세 regularPrice/discountedPrice 등 실제 금액 사용).
 * 켜기: `REACT_APP_ORDER_CONFIRM_FIXED_KRW=1000` 처럼 양수 설정.
 */
function getDevOrderConfirmFixedKrw(): number | null {
    if (process.env.NODE_ENV !== 'development') return null;
    const raw = process.env.REACT_APP_ORDER_CONFIRM_FIXED_KRW;
    if (raw == null || String(raw).trim() === '' || raw === '0' || raw === 'false') return null;
    const n = Number(String(raw).trim());
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/** Auth의 organizationId(문자열·ORG 접두 등) → API용 숫자 */
function parseOrganizationIdToNumeric(raw: string | number | null | undefined): number {
    if (raw == null || raw === '') return 0;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.floor(raw);
    const stripped = String(raw).replace(/^ORG/i, '').trim();
    const parsed = parseInt(stripped, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export default function OrderConfirm(): React.ReactElement {
    const { t, i18n } = useTranslation('order');
    const navigate = useNavigate();
    const location = useLocation();
    const { user, getCurrentRole } = useAuth();
    const role = getCurrentRole();
    /** 오픈클래스는 POST /order/open-class 학생·마스터 동일 플로우(orderService.purchaseOpenClass) */
    const canPurchaseOpenClassLikeStudent = role === 'student' || role === 'master';

    const state = location.state as OrderConfirmState | undefined;

    const classIdsForFetch = useMemo(() => {
        if (!state) return [] as string[];
        const s = new Set<string>();
        for (const id of state.selectedClassIds ?? []) s.add(String(id));
        for (const row of state.items ?? []) s.add(String(row.classId));
        return [...s].filter((idStr) => {
            const n = parseInt(idStr, 10);
            return Number.isFinite(n) && n > 0;
        });
    }, [state]);

    const apiLang = getApiLanguageCode(i18n.language);
    // TODO: 용도에 맞는 API인지 확인 필요
    // 문제: 주문 확인 데이터에는 classId만 있고 클래스 상세 정보(가격, 제목 등)가 없음
    // → N개 주문 아이템마다 클래스 상세 API N개 호출 (N+1 문제)
    const detailQueries = useQueries({
        queries: classIdsForFetch.map((idStr) => {
            const idNum = parseInt(idStr, 10);
            return {
                queryKey: [...classKeys.detail(idNum), apiLang, role === 'admin' ? 'catalogMerge' : 'noCatalogMerge'],
                queryFn: ({ signal }: { signal?: AbortSignal }) =>
                    getClassDetail(idNum, {
                        signal,
                        language: apiLang,
                    }),
                enabled: Number.isFinite(idNum) && idNum > 0,
                staleTime: 60_000,
            };
        }),
    });

    const classes = useMemo(() => {
        const m: Record<string, ClassItem> = {};
        classIdsForFetch.forEach((idStr, i) => {
            const dto = detailQueries[i]?.data;
            if (dto) {
                m[idStr] = classDetailDtoToClassItem(dto, {
                    organizationName: dto.organizationTitle ?? undefined,
                });
            }
        });
        return m;
    }, [classIdsForFetch, detailQueries]);

    /** 초기 로딩만 — 백그라운드 refetch(isFetching)는 결제 차단하지 않음 */
    const detailsInitialLoading =
        classIdsForFetch.length > 0 &&
        detailQueries.length > 0 &&
        detailQueries.some((q) => q.isLoading);
    /** 로딩 종료 후에도 일부 클래스 상세가 없으면(401·404 등) 주문 불가 */
    const detailsMissingAfterLoad =
        classIdsForFetch.length > 0 &&
        detailQueries.length === classIdsForFetch.length &&
        detailQueries.every((q) => !q.isLoading) &&
        classIdsForFetch.some((_, i) => !detailQueries[i]?.data);
    const firstDetailErr = detailQueries.find((q) => q.isError)?.error;
    const firstDetailErrorMessage = firstDetailErr instanceof Error ? firstDetailErr.message : null;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [khaLoginModalOpen, setKhaLoginModalOpen] = useState(false);
    const [khaLoginForm, setKhaLoginForm] = useState({ loginId: '', password: '' });
    const [, setKhaMemberStatus] = useState<'none' | 'verified' | 'full'>(() => {
        const raw = localStorage.getItem(KHA_MEMBER_STATUS_KEY);
        return raw === 'verified' || raw === 'full' ? raw : 'none';
    });

    // 수강신청자 정보
    const [applicantInfo, setApplicantInfo] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        organization: '',
        jobRole: ''
    });

    // state가 없으면 공통 장바구니로 리다이렉트
    useEffect(() => {
        const hasItems = state?.items?.length;
        const hasIds = state?.selectedClassIds?.length;
        if (!state || (!hasItems && !hasIds)) {
            alert(t('confirm.invalidAccess'));
            navigate(ROUTES.COMMON.CART);
        }
    }, [state, navigate, t]);

    // 수강신청자 정보 불러오기 (user 컨텍스트 + localStorage)
    useEffect(() => {
        const savedJobRole = localStorage.getItem('userJobRole') || '';
        const savedCustomJobRole = localStorage.getItem('userCustomJobRole') || '';
        const finalJobRole = savedJobRole === '직접입력' ? savedCustomJobRole : savedJobRole;
        setApplicantInfo({
            name: user?.name || '',
            email: user?.email || '',
            phoneNumber: localStorage.getItem('userPhoneNumber') || '',
            organization: user?.currentAccount?.organizationName || '',
            jobRole: finalJobRole,
        });
    }, [user]);

    const orderItems = useMemo((): Array<{ class: ClassItem; quantity: number }> => {
        if (!state) return [];
        const stateItems = state.items;
        const selectedClassIds = state.selectedClassIds ?? [];
        if (stateItems?.length) {
            return stateItems
                .map(({ classId, quantity }) => {
                    const c = classes[classId];
                    return c ? { class: c, quantity } : null;
                })
                .filter(Boolean) as Array<{ class: ClassItem; quantity: number }>;
        }
        return selectedClassIds
            .map((id) => {
                const c = classes[id];
                return c ? { class: c, quantity: 1 } : null;
            })
            .filter(Boolean) as Array<{ class: ClassItem; quantity: number }>;
    }, [state, classes]);

    const selectedClassIdsList = useMemo(() => {
        if (!state) return [] as string[];
        return state.items?.map((i) => i.classId) ?? state.selectedClassIds ?? [];
    }, [state]);

    /** 학생·마스터·오픈클래스만 토스 없이 즉시 완료(주문 PATCH PAID) 가능 */
    const isStudentOpenClassOrder =
        canPurchaseOpenClassLikeStudent &&
        orderItems.length > 0 &&
        orderItems.every(({ class: c }) => c.classType === 'open');
    const checkoutWithoutToss = isStudentOpenClassOrder && !isTossCheckoutEnabled();

    /** 상세 조회 후 ClassItem 가격이 채워지면 그 합계를 우선(이전 경로에서 totalPrice가 0으로 넘어온 경우 보정) */
    const payableFromItems = useMemo(
        () =>
            orderItems.reduce((sum, { class: c, quantity }) => {
                const unit = c.discountPrice !== undefined ? c.discountPrice : (c.price || 0);
                return sum + unit * quantity;
            }, 0),
        [orderItems]
    );

    if (!state) {
        return <></>;
    }

    const totalPriceFromNav =
        typeof state.totalPrice === 'number' && Number.isFinite(state.totalPrice) ? state.totalPrice : 0;
    const devFixedKrw = getDevOrderConfirmFixedKrw();
    const useDevFixedPricing = devFixedKrw != null && orderItems.length > 0;

    const baseOriginalTotal = orderItems.reduce((sum, { class: item, quantity }) => sum + (item.price || 0) * quantity, 0);
    const baseDiscountTotal = orderItems
        .filter(({ class: item }) => item.discountPrice !== undefined)
        .reduce((sum, { class: item, quantity }) => sum + ((item.price || 0) - (item.discountPrice || 0)) * quantity, 0);

    const originalTotal = useDevFixedPricing ? devFixedKrw : baseOriginalTotal;
    const discountTotal = useDevFixedPricing ? 0 : baseDiscountTotal;
    const displayedFinalAmount = useDevFixedPricing
        ? devFixedKrw
        : orderItems.length > 0
          ? payableFromItems
          : totalPriceFromNav;

    const handleKhaVerify = () => setKhaLoginModalOpen(true);
    const handleKhaLoginSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!khaLoginForm.loginId.trim() || !khaLoginForm.password.trim()) {
            alert(t('khaModal.khaLoginAlert'));
            return;
        }
        localStorage.setItem(KHA_MEMBER_STATUS_KEY, 'full');
        setKhaMemberStatus('full');
        setKhaLoginModalOpen(false);
        setKhaLoginForm({ loginId: '', password: '' });
    };

    const handleFinalCheckout = async () => {
        if (!agreeTerms) {
            alert(t('confirm.agreePolicyAlert'));
            return;
        }
        if (detailsInitialLoading) {
            alert('클래스 정보를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.');
            return;
        }
        if (detailsMissingAfterLoad) {
            alert(
                firstDetailErrorMessage
                    ? `클래스 정보를 불러오지 못했습니다.\n${firstDetailErrorMessage}`
                    : '클래스 정보를 불러오지 못해 주문을 진행할 수 없습니다.'
            );
            return;
        }
        if (orderItems.length === 0) {
            alert('주문할 클래스 정보가 없습니다. 장바구니에서 다시 진행해 주세요.');
            return;
        }
        const isStudentOpenClassCheckout =
            canPurchaseOpenClassLikeStudent &&
            orderItems.length > 0 &&
            orderItems.every(({ class: c }) => c.classType === 'open');

        if (!isStudentOpenClassCheckout) {
            const missingPrice = orderItems.find(({ class: c }) => !c.productPriceId || c.productPriceId <= 0);
            if (missingPrice) {
                alert('판매 단위(productPrice)가 없는 클래스가 있어 주문을 등록할 수 없습니다. 클래스 상세를 확인해 주세요.');
                return;
            }
        }

        const orgFromUser = parseOrganizationIdToNumeric(user?.currentAccount?.organizationId);
        const orgFromClasses = orderItems
            .map(({ class: c }) => c.organizationId)
            .filter((id): id is number => typeof id === 'number' && id > 0);
        const orgFromDetailApis = detailQueries
            .map((q) => {
                const d = q.data;
                return d && typeof d.organizationId === 'number' && d.organizationId > 0 ? d.organizationId : 0;
            })
            .filter((id) => id > 0);
        /** 판매(클래스) 기관 우선 — 학생 소속 기관과 다를 때 POST가 거절되는 경우 완화 */
        const sellerOrg = orgFromDetailApis[0] ?? orgFromClasses[0] ?? 0;
        const organizationId = sellerOrg > 0 ? sellerOrg : orgFromUser;
        if (!isStudentOpenClassCheckout && organizationId <= 0) {
            alert('주문에 필요한 기관 정보를 찾을 수 없습니다. 계정 기관 또는 클래스 기관 정보를 확인해 주세요.');
            return;
        }

        const devFix = getDevOrderConfirmFixedKrw();
        const useDevFix = devFix != null && orderItems.length > 0;

        let items = orderItems.map(({ class: c, quantity }) => {
            const list = c.price ?? 0;
            const disc = c.discountPrice;
            const unitPrice = list > 0 ? list : disc ?? 0;
            const discountPrice = list > 0 && disc != null && disc < list ? disc : undefined;
            return {
                productPriceId: c.productPriceId!,
                quantity,
                unitPrice,
                ...(discountPrice != null ? { discountPrice } : {}),
            };
        });

        if (useDevFix && !isStudentOpenClassCheckout) {
            const seats = items.reduce((s, it) => s + it.quantity, 0) || 1;
            const per = Math.max(1, Math.floor(devFix! / seats));
            items = items.map((it) => ({ ...it, unitPrice: per }));
        }

        if (!isStudentOpenClassCheckout && !useDevFix && items.some((it) => it.unitPrice <= 0)) {
            alert('클래스 판매 단가가 0입니다. 클래스 상세에서 가격 정보를 확인한 뒤 다시 시도해 주세요.');
            return;
        }

        const apiTotal = orderItems.reduce((sum, { class: c, quantity }) => {
            const unit = c.discountPrice !== undefined ? c.discountPrice : (c.price || 0);
            return sum + unit * quantity;
        }, 0);
        const totalAmount = useDevFix ? devFix! : apiTotal > 0 ? apiTotal : totalPriceFromNav;

        if (isStudentOpenClassCheckout && totalAmount <= 0) {
            alert('결제 금액이 0입니다. 클래스 가격 정보를 확인한 뒤 다시 시도해 주세요.');
            return;
        }

        const dealerTitle =
            orderItems.find(({ class: c }) => c.organizationName?.trim())?.class.organizationName?.trim() ?? undefined;

        const openClassIdsPayload = orderItems.flatMap(({ class: c, quantity }) => {
            const id = parseInt(String(c.id), 10);
            if (!Number.isFinite(id) || id <= 0) return [] as number[];
            return Array.from({ length: Math.max(1, quantity) }, () => id);
        });

        /** 학생 오픈클래스이고 토스를 쓰지 않을 때(키 없음 또는 REACT_APP_DISABLE_TOSS_CHECKOUT) */
        const skipTossCard = isStudentOpenClassCheckout && !isTossCheckoutEnabled();
        if (!skipTossCard && !isTossCheckoutEnabled()) {
            alert(t('confirm.tossPayUnavailable'));
            return;
        }
        if (!skipTossCard && !getTossClientKey()) {
            alert(t('confirm.tossClientKeyMissing'));
            return;
        }

        const tossKey = getTossClientKey();

        setIsSubmitting(true);
        try {
            let created: { orderId: number };
            let orderNumberForRecent = '';
            let tossMerchantOrderIdForToss: string;

            if (isStudentOpenClassCheckout) {
                if (openClassIdsPayload.length === 0) {
                    alert('유효한 클래스 ID가 없습니다.');
                    return;
                }
                const openRes = await orderService.purchaseOpenClass({
                    classIds: openClassIdsPayload,
                    language: apiLang,
                });
                created = { orderId: openRes.orderId };
                orderNumberForRecent = openRes.orderNumber?.trim() || `ORD-${openRes.orderId}`;
                tossMerchantOrderIdForToss = tossMerchantOrderIdFromOpenClassOrderNumber(
                    openRes.orderNumber,
                    openRes.orderId
                );
            } else {
                created = await orderService.create({
                    organizationId,
                    downPayment: totalAmount,
                    interimPayment: 0,
                    finalPayment: 0,
                    ...(dealerTitle ? { dealerOrganizationTitle: dealerTitle } : {}),
                    items,
                    totalAmount,
                    isLicenseActive: false,
                });

                try {
                    await orderService.update(created.orderId, {
                        organizationId,
                        downPayment: totalAmount,
                        interimPayment: 0,
                        finalPayment: 0,
                        ...(dealerTitle ? { dealerOrganizationTitle: dealerTitle } : {}),
                        items,
                        totalAmount,
                        isLicenseActive: false,
                        paymentStatus: 'UN_INVOICED',
                    });
                } catch {}
                orderNumberForRecent = String(created.orderId);
                tossMerchantOrderIdForToss = buildTossMerchantOrderId(created.orderId);
            }

            const getRecentOrderDisplayFields = () => {
                const org =
                    dealerTitle?.trim() ||
                    orderItems.find(({ class: c }) => c.organizationName?.trim())?.class.organizationName?.trim() ||
                    applicantInfo.organization?.trim() ||
                    undefined;
                const userName = applicantInfo.name?.trim() || undefined;
                const productNames = orderItems
                    .map(({ class: c, quantity }) => {
                        const display = getLocalizedClassItem(c, i18n.language);
                        const title = display.title?.trim();
                        if (!title) return null;
                        return quantity > 1 ? `${title} ×${quantity}` : title;
                    })
                    .filter((x): x is string => Boolean(x));
                return {
                    organizationName: org ?? null,
                    userName: userName ?? null,
                    ...(productNames.length > 0 ? { productNames } : {}),
                };
            };

            const recordRecentStudentOrder = (paymentStatus: ApiPaymentStatus) => {
                pushRecentStudentOrderSummary({
                    orderId: created.orderId,
                    orderNumber: orderNumberForRecent || String(created.orderId),
                    totalAmount,
                    createdAt: new Date().toISOString(),
                    paymentStatus,
                    ...getRecentOrderDisplayFields(),
                });
            };

            if (skipTossCard && isStudentOpenClassCheckout) {
                /**
                 * 기본: 상세 GET으로 PATCH 본문 구성. 일부 환경에서는 학생에게 GET /order/{id}/detail 이 403이라
                 * 이미 주문 확정 화면에서 계산한 items·organizationId·totalAmount 로 PAID PATCH만 시도한다.
                 */
                try {
                    const detail = await orderService.getDetail(created.orderId, apiLang);
                    await orderService.update(
                        created.orderId,
                        buildUpdateOrderDtoFromDetail(detail, { paymentStatus: 'PAID' })
                    );
                } catch (detailErr) {
                    const forbidden =
                        detailErr instanceof ApiError && (detailErr.status === 403 || detailErr.status === 401);
                    if (
                        !forbidden ||
                        organizationId <= 0 ||
                        !items.length ||
                        items.some((it) => !it.productPriceId || it.productPriceId <= 0)
                    ) {
                        throw detailErr;
                    }
                    await orderService.update(created.orderId, {
                        organizationId,
                        downPayment: totalAmount,
                        interimPayment: 0,
                        finalPayment: 0,
                        ...(dealerTitle ? { dealerOrganizationTitle: dealerTitle } : {}),
                        items,
                        totalAmount,
                        isLicenseActive: false,
                        paymentStatus: 'PAID',
                    });
                }
                recordRecentStudentOrder('PAID');
                const ids = selectedClassIdsList;
                const cartEntries = parseCartStorage(localStorage.getItem('cart'));
                const removeIds = new Set(ids.map(String));
                persistCartItems(cartEntries.filter((e) => !removeIds.has(String(e.id))));
                window.dispatchEvent(new Event('cart-updated'));
                try {
                    await Promise.all(
                        ids.map(async (rawId) => {
                            const n = parseInt(String(rawId), 10);
                            if (!Number.isFinite(n) || n <= 0) return;
                            await enrollClass(n);
                        })
                    );
                } catch (error) {
                    alert(
                        error instanceof Error
                            ? `클래스 등록 실패: ${error.message}`
                            : '클래스 등록에 실패했습니다. 고객센터로 문의해 주세요.'
                    );
                    return;
                }
                await invalidateEnrolledClassListQueries(queryClient);
                await queryClient.invalidateQueries({ queryKey: ['orders'], exact: false });
                alert(t('confirm.directCompleteSuccess'));
                navigate(
                    role === 'master' ? `${ROUTES.MASTER.BASE}/orders` : `${ROUTES.STUDENT.BASE}/orders`
                );
                return;
            }

            recordRecentStudentOrder('UN_INVOICED');
            const tossMerchantOrderId = tossMerchantOrderIdForToss;
            saveTossCheckoutPending({
                medicrewOrderId: created.orderId,
                tossMerchantOrderId,
                amount: totalAmount,
                selectedClassIds: selectedClassIdsList,
                redirectBase: role === 'master' ? 'master' : 'student',
                checkoutSnapshot: buildCheckoutSnapshot(orderItems),
            });
            const orderName =
                orderItems.length === 1
                    ? (orderItems[0].class.title?.trim().slice(0, 100) || '클래스')
                    : `클래스 ${orderItems.length}건`;
            if (!tossKey) {
                alert(t('confirm.tossClientKeyMissing'));
                return;
            }
            await requestTossCardCheckout({
                clientKey: tossKey,
                amount: totalAmount,
                tossMerchantOrderId,
                orderName,
                customerName: applicantInfo.name?.trim() || '고객',
            });
        } catch (e) {
            alert(e instanceof Error ? e.message : '주문 등록에 실패했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-[1280px] mx-auto px-4 py-8">
            <BackToListLink onClick={() => navigate(-1)} className="mb-4">
                {t('confirm.back')}
            </BackToListLink>
            <PageHeader
                title={t('confirm.pageTitle')}
            />
            {useDevFixedPricing && (
                <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    [개발] 주문 금액 고정: {formatCurrency(devFixedKrw, 'KRW')} (실서비스 빌드에서는 비활성)
                </p>
            )}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 주문 내역 */}
                <div className="lg:col-span-2 space-y-6">
                    {/* 신청 클래스 목록 */}
                    <div className="bg-white rounded-lg border border-gray-400 shadow-sm p-6">
                        <h2 className="text-base font-semibold text-gray-900 mb-4">{t('confirm.applyClasses')}</h2>
                        {detailsInitialLoading && (
                            <p className="text-sm text-gray-500 mb-4">클래스 정보를 불러오는 중입니다…</p>
                        )}
                        {detailsMissingAfterLoad && (
                            <p className="text-sm text-red-600 mb-4">
                                클래스 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.
                                {firstDetailErrorMessage ? ` (${firstDetailErrorMessage})` : ''}
                            </p>
                        )}
                        <div className="space-y-4">
                            {orderItems.map(({ class: item, quantity }) => {
                                const displayItem = getLocalizedClassItem(item, i18n.language);
                                const unitPrice = item.discountPrice !== undefined ? item.discountPrice : (item.price || 0);
                                const lineTotal =
                                    useDevFixedPricing && orderItems.length === 1
                                        ? devFixedKrw!
                                        : unitPrice * quantity;
                                return (
                                    <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-200 last:border-0 last:pb-0">
                                        <div className="w-24 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                            {item.thumbnail ? (
                                                <img src={item.thumbnail} alt={displayItem.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No Image</div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-base font-bold text-gray-900 mb-1">{displayItem.title}</h3>
                                            {displayItem.organizationName && (
                                                <p className="text-sm text-gray-500 mb-2">{displayItem.organizationName}</p>
                                            )}
                                            {quantity > 1 && (
                                                <p className="text-sm text-gray-500">{t('confirm.quantityLabel')}: {quantity}{t('confirm.countUnit')}</p>
                                            )}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            {item.discountPrice !== undefined && !useDevFixedPricing ? (
                                                <>
                                                    <div className="text-sm text-gray-400 line-through mb-1">
                                                        {formatCurrencyForLocale(item.price, i18n.language)} {quantity > 1 && `× ${quantity}`}
                                                    </div>
                                                    <div className="text-base font-semibold text-blue-500">
                                                        {formatCurrencyForLocale(lineTotal, i18n.language)}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-base font-semibold text-gray-800">
                                                    {formatCurrencyForLocale(lineTotal, i18n.language)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* 주문자 정보 */}
                        <div className="border-t border-gray-200 pt-6 mt-6">
                            {/* 타이틀과 수정하기 버튼 */}
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-base font-semibold text-gray-800">{t('confirm.applicantInfo')}</h2>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-sm font-semibold"
                                    onClick={() => navigate('/settings')}
                                >
                                    {t('confirm.editButton')}
                                </Button>
                            </div>
                            <div className="space-y-4">
                                {/* 이름 */}
                                <div className="flex items-center font-medium gap-3">
                                    <User size={16} className="text-gray-500 flex-shrink-0" />
                                    <div className="flex flex-1 items-center">
                                        <div className="text-sm text-gray-500 w-24 flex-shrink-0">{t('confirm.name')}</div>
                                        <div className="text-sm text-gray-800">
                                            {applicantInfo.name || t('confirm.noInfo')}
                                        </div>
                                    </div>
                                </div>

                                {/* 이메일 */}
                                <div className="flex items-center font-medium gap-3">
                                    <Mail size={16} className="text-gray-500 flex-shrink-0" />
                                    <div className="flex flex-1 items-center">
                                        <div className="text-sm text-gray-500 w-24 flex-shrink-0">{t('confirm.email')}</div>
                                        <div className="text-sm text-gray-800">
                                            {applicantInfo.email || t('confirm.noInfo')}
                                        </div>
                                    </div>
                                </div>

                                {/* 휴대폰번호 */}
                                <div className="flex items-center font-medium gap-3">
                                    <Phone size={16} className="text-gray-500 flex-shrink-0" />
                                    <div className="flex flex-1 items-center">
                                        <div className="text-sm text-gray-500 w-24 flex-shrink-0">{t('confirm.phoneNumber')}</div>
                                        <div className="text-sm text-gray-800">
                                            {applicantInfo.phoneNumber || t('confirm.noInfo')}
                                        </div>
                                    </div>
                                </div>

                                {/* 소속기관 */}
                                <div className="flex items-center font-medium gap-3">
                                    <Building2 size={16} className="text-gray-500 flex-shrink-0" />
                                    <div className="flex flex-1 items-center">
                                        <div className="text-sm text-gray-500 w-24 flex-shrink-0">{t('confirm.organization')}</div>
                                        <div className="text-sm text-gray-800">
                                            {applicantInfo.organization || t('confirm.noInfo')}
                                        </div>
                                    </div>
                                </div>

                                {/* 직무 */}
                                <div className="flex items-center font-medium gap-3">
                                    <Briefcase size={16} className="text-gray-500 flex-shrink-0" />
                                    <div className="flex flex-1 items-center">
                                        <div className="text-sm text-gray-500 w-24 flex-shrink-0">{t('confirm.jobRole')}</div>
                                        <div className="text-sm text-gray-800">
                                            {applicantInfo.jobRole || t('confirm.noInfo')}
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* 서비스 이용 및 환불 정책 동의 */}
                        <div className="border-t border-gray-200 pt-6 mt-6">
                            <h2 className="text-base font-semibold text-gray-800 mb-4">{t('confirm.policyTitle')}</h2>
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <ul className="space-y-1 text-sm text-gray-700">
                                    <li>• {t('confirm.policy1')}</li>
                                    <li>• {t('confirm.policy2')}</li>
                                    <li>• {t('confirm.policy3')}</li>
                                    <li>• {t('confirm.policy4')}</li>
                                    <li>• {t('confirm.policy5')}</li>
                                    <li>• {t('confirm.policy6')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 결제 정보 요약 */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg border border-gray-400 shadow-sm p-6 sticky top-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                            {t('confirm.paymentInfo')}
                        </h3>

                        <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">{t('confirm.selectedClasses')}</span>
                                <span className="font-medium text-gray-900">
                                    {orderItems.reduce((acc, { quantity }) => acc + quantity, 0)} {t('confirm.countUnit')}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">{t('confirm.totalProductAmount')}</span>
                                <span className="font-medium text-gray-900">
                                    {formatCurrencyForLocale(originalTotal, i18n.language)}
                                </span>
                            </div>
                            {discountTotal > 0 && (
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">{t('confirm.discountAmount')}</span>
                                    <span className="font-medium text-red-600">
                                        -{formatCurrencyForLocale(discountTotal, i18n.language)}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between mb-6 pt-2">
                            <span className="text-sm font-bold text-gray-900">{t('confirm.finalAmount')}</span>
                            <span className="text-base font-bold text-blue-500">
                                {formatCurrencyForLocale(displayedFinalAmount, i18n.language)}
                            </span>
                        </div>

                        <label className="flex items-center gap-2 mb-4 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-600"
                            />
                            <span className="text-sm text-gray-700">{t('confirm.agreeLabel')}</span>
                        </label>

                        <div className="space-y-2">
                            <Button
                                variant="primary"
                                size="md"
                                className="w-full"
                                onClick={() => void handleFinalCheckout()}
                                disabled={
                                    !agreeTerms ||
                                    isSubmitting ||
                                    detailsInitialLoading ||
                                    detailsMissingAfterLoad ||
                                    orderItems.length === 0
                                }
                            >
                                {isSubmitting ? '처리 중…' : checkoutWithoutToss ? t('confirm.completeOrderDirect') : t('confirm.onlinePayment')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={khaLoginModalOpen}
                onClose={() => {
                    setKhaLoginModalOpen(false);
                    setKhaLoginForm({ loginId: '', password: '' });
                }}
                title={t('khaModal.title')}
                size="medium"
                maxWidth="max-w-[420px]"
            >
                <div className="px-10 py-4 space-y-5">
                    <div className="flex justify-center">
                        <img
                            src="/kha-logo.png"
                            alt="사단법인 대한병원협회"
                            className="h-14 w-auto object-contain"
                        />
                    </div>
                    <p className="text-center text-gray-600">{t('khaModal.description')}</p>
                    <form onSubmit={handleKhaLoginSubmit} className="space-y-4">
                        <Input
                            label={t('khaModal.loginId')}
                            value={khaLoginForm.loginId}
                            onChange={(e) => setKhaLoginForm(f => ({ ...f, loginId: e.target.value }))}
                            placeholder={t('khaModal.loginIdPlaceholder')}
                            autoComplete="username"
                            wrapperClassName="w-full"
                        />
                        <Input
                            label={t('khaModal.password')}
                            type="password"
                            value={khaLoginForm.password}
                            onChange={(e) => setKhaLoginForm(f => ({ ...f, password: e.target.value }))}
                            placeholder={t('khaModal.passwordPlaceholder')}
                            autoComplete="current-password"
                            wrapperClassName="w-full"
                        />
                        <Button
                            type="submit"
                            variant="primary"
                            size="md"
                            className="w-full inline-flex items-center justify-center gap-2"
                        >
                            {t('khaModal.loginButton')}
                        </Button>
                    </form>
                    <p className="text-center">
                        <a
                            href="https://khaedu.or.kr/Membership/Find"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                            {t('khaModal.findAccount')}
                        </a>
                    </p>
                </div>
            </Modal>
        </div>
    );
}
